import {
  PlaneTakeoff,
  Building2,
  CarFront,
  Gem,
  Bus,
  type LucideIcon,
} from "lucide-react";

interface OperatorCategory {
  /** The lucide glyph that suggests this operator category. */
  icon: LucideIcon;
  /** A generic, illustrative category label — never a real company name. */
  label: string;
}

/**
 * Generic, illustrative operator CATEGORIES — deliberately not company names.
 * We have no real customer logos, so the strip shows the *kinds* of operators
 * the platform serves rather than fabricating brands or statistics.
 */
const categories: OperatorCategory[] = [
  { icon: PlaneTakeoff, label: "Airport transfers" },
  { icon: Building2, label: "Corporate fleets" },
  { icon: CarFront, label: "Black-car services" },
  { icon: Gem, label: "Limo & charter" },
  { icon: Bus, label: "Shuttle & group" },
];

/**
 * A slim, low-contrast credibility strip that sits just beneath the homepage
 * hero. Rather than fabricate customer logos or statistics (we have neither),
 * it reads as a precision "spec sheet": a quiet qualitative lead followed by a
 * monochrome row of generic operator *category* labels — lucide glyph + label,
 * separated by hairline tick dividers, like a control-panel legend.
 *
 * Purely presentational: no props, no interactivity, no data. Flip-safe across
 * light/dark (neutral ladder + semantic surface tokens only); the row wraps
 * gracefully on narrow viewports.
 *
 * @returns The trust / credibility strip.
 */
export function TrustLogosBar() {
  return (
    <section
      aria-label="Who RideReserv is built for"
      className="border-y border-neutral-200 bg-background"
    >
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-x-10 gap-y-4 px-4 py-6 sm:px-6 lg:flex-row lg:justify-between lg:px-8">
        {/* Qualitative lead — the only slightly-raised element. No numbers. */}
        <p className="shrink-0 text-center text-sm font-medium text-neutral-600 lg:text-left">
          Built for professional operators of every kind.
        </p>

        {/* Monochrome category legend. Items are spaced with `gap` only (no
            inter-item separator elements) so the row wraps cleanly on narrow
            viewports without any orphaned dividers. Generous horizontal gap
            keeps the "control-panel legend" read. Not real brands. */}
        <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 sm:gap-x-8">
          {categories.map(({ icon: Icon, label }) => (
            <li
              key={label}
              className="inline-flex items-center gap-2 text-neutral-400 transition-colors hover:text-neutral-500"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
              <span className="text-xs font-medium uppercase tracking-wide">
                {label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
